import { ClickEventComponent, ComponentIds, ComponentUtils, MathUtils, Vector, ViewerDragEventComponent, } from "@inter-ikea-core/core";
import { rgb, toVector, toMeters } from "../util";
export class IPEXViewerObject {
    id;
    viewer;
    entityManager;
    entity;
    get hasRegisteredInputEvents() {
        return this._hasRegisteredInputEvents;
    }
    _hasRegisteredInputEvents = false;
    advancedViewer;
    constructor(id, viewer, entityManager, entity) {
        this.id = id;
        this.viewer = viewer;
        this.entityManager = entityManager;
        this.entity = entity;
        this.advancedViewer = viewer;
        viewer.set_visible(id, false, true);
    }
    deleteObject() {
        if (this.viewer.object_exists(this.id)) {
            return this.viewer.delete_object(this.id, true);
        }
        return false;
    }
    getViewerObjectId() {
        return this.id;
    }
    setPosition(v) {
        this.viewer.set_position(this.id, toMeters(v.X), toMeters(v.Y), toMeters(v.Z));
        return this;
    }
    getPosition() {
        return toVector(this.viewer.get_position(this.id));
    }
    setRotation(q) {
        const v = q.toEuler();
        this.viewer.set_rotation(this.id, v.X, v.Y, v.Z);
        return this;
    }
    getRotation() {
        const viewerRotation = this.viewer.get_rotation(this.id);
        return MathUtils.quaternionFromEulerRadians(viewerRotation.x, viewerRotation.y, viewerRotation.z);
    }
    setRotationEulerAngleDegrees(v) {
        this.viewer.set_rotation(this.id, (v.X * Math.PI) / 180, (v.Y * Math.PI) / 180, (v.Z * Math.PI) / 180);
        return this;
    }
    setScale(x, y, z) {
        this.viewer.set_scale(this.id, x, y, z);
        return this;
    }
    setScaleV(scale) {
        return this.setScale(scale.X, scale.Y, scale.Z);
    }
    getScale() {
        const scale = this.viewer.get_scale(this.id);
        return new Vector(scale.x, scale.y, scale.z);
    }
    setOutlined(enable) {
        this.viewer.set_outline(this.id, enable, true);
        return this;
    }
    setBlockOutlined(enabled) {
        this.viewer.set_outline_block(this.id, enabled, true);
        return this;
    }
    setBaseColor(r, g, b) {
        const color = rgb(r, g, b);
        this.viewer.get_children(this.id, false, true).forEach((childId) => {
            const materialId = this.viewer.get_material(childId);
            if (materialId != -1) {
                this.viewer.set_base_color(materialId, color);
            }
        });
        return this;
    }
    setTransparency(alpha) {
        this.viewer.set_opacity(this.viewer.get_material(this.id), alpha);
        return this;
    }
    setReceiveShadows(receiveShadows) {
        this.viewer.set_receive_shadow(this.id, receiveShadows, true);
        return this;
    }
    setCastShadows(castShadows) {
        this.viewer.set_cast_shadow(this.id, castShadows, true);
        return this;
    }
    setVisible(visible) {
        this.viewer.set_visible(this.id, visible, true);
        return this;
    }
    getVisible() {
        return this.viewer.get_visible(this.id);
    }
    setEnabled(enabled) {
        this.advancedViewer.set_enabled(this.id, enabled);
    }
    isEnabled() {
        return this.advancedViewer.get_enabled(this.id);
    }
    setParent(parentViewerObject) {
        const parentId = parentViewerObject instanceof IPEXViewerObject
            ? parentViewerObject.getViewerObjectId()
            : null;
        this.viewer.set_parent(this.id, parentId);
        return this;
    }
    sendDragEvent = (_objectId, event) => {
        const entity = this.entity;
        if (!entity)
            return;
        const draggedEntity = ComponentUtils.draggableRootEntity(entity) || entity;
        const planePoint = toVector(event.plane_point);
        const planeNormal = new Vector(event.plane_normal.x, event.plane_normal.y, event.plane_normal.z);
        const delta = toVector(event.world_delta);
        const cameraPosition = toVector(this.viewer.get_active_camera()?.position || { x: 0, y: 0, z: 0 });
        const component = new ViewerDragEventComponent(planePoint, planeNormal, delta, cameraPosition);
        this.entityManager.addComponent(draggedEntity, component);
    };
    sendDragEndEvent = () => {
        const entity = this.entity;
        if (!entity)
            return;
        const draggedEntity = ComponentUtils.draggableRootEntity(entity) || entity;
        this.entityManager.removeComponent(draggedEntity, ComponentIds.VIEWER_DRAG_EVENT_COMPONENT_ID);
    };
    sendClickEvent = (_senderId, event) => {
        const entity = this.entity;
        if (!entity)
            return;
        const screenCoords = new Vector(event.canvas_x, event.canvas_y, 0);
        const component = new ClickEventComponent(screenCoords);
        this.entityManager.addComponent(entity, component);
        this.entityManager.removeComponent(entity, ComponentIds.CLICK_EVENT_COMPONENT_ID);
    };
    observers = [];
    /**
     * Will register both drag and click events for this object and all its descendants.
     *
     * Note that if you change the hierarchy and want to update the observers, you need to
     * first unregister the old ones by calling {@link unregisterInputEvents} and then call
     * this method again.
     */
    registerInputEvents() {
        // We can't send events about this object if it doesn't correspond to an entity.
        if (!this.entity)
            return;
        if (this._hasRegisteredInputEvents)
            return;
        this.observers = this.viewer.get_children(this.id, false, true).flatMap((childId) => [
            this.viewer.get_object_click_observable(childId).add(this.sendClickEvent),
            this.viewer.get_object_drag_begin_observable(childId).add(this.sendDragEvent),
            this.viewer.get_object_drag_observable(childId).add(this.sendDragEvent),
            this.viewer.get_object_drag_end_observable(childId).add(this.sendDragEndEvent),
        ]);
        this._hasRegisteredInputEvents = true;
    }
    /**
     * Enables consumers to avoid triggering input events. Note that if you just remove DraggableComponent
     * or SelectableComponent event components will still be generated and will "bubble up" in the hierarchy
     * until it finds one of these components. So in case what you actually want to do is just not get
     * input events then the new method is the recommended way.
     */
    unregisterInputEvents() {
        // We can't send events about this object if it doesn't correspond to an entity.
        if (!this.entity)
            return;
        if (!this._hasRegisteredInputEvents)
            return;
        this.observers.forEach((observer) => observer.dispose());
        this.observers = [];
        this._hasRegisteredInputEvents = false;
    }
    makeClickable(onClick) {
        this.viewer.get_object_click_observable(this.id).add(onClick);
    }
    setOutlinedObjectColor(r, g, b) {
        this.viewer.set_object_outline_color(this.id, rgb(r, g, b));
        return this;
    }
    removeOutlinedObjectColor() {
        this.viewer.set_object_outline_color(this.id, null);
        return this;
    }
}
//# sourceMappingURL=IPEXViewerObject.js.map